import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import imgBg1 from "../../img/prestations/prestation1.webp";
import imgBg2 from "../../img/prestations/prestation2.webp";
import imgBg3 from "../../img/prestations/prestation3.webp";
import { NavLink } from "react-router-dom";

interface PrestaProps {
  id: number;
  title: string;
  duration: string;
  description: string;
  price: string;
  includedServices: string[];
  characteristics: string[];
  bgImage: number;
}

const images = [imgBg1, imgBg2, imgBg3];

const PrestaCard: React.FC<PrestaProps> = ({
  id,
  title,
  duration,
  description,
  price,
  includedServices,
  characteristics,
  bgImage,
}) => {
  const cardRef = useRef(null);
  const titleRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, amount: 0.2 });
  const isTitleInView = useInView(titleRef, { once: true, amount: 0.5 });

  //#region Animation Variants
  // Animation variants pour la card
  const cardVariants = {
    hidden: {opacity: 0, y: 50, scale: 0.95 },
    visible: {opacity: 1, y: 0, scale: 1, transition: {duration: 0.6, ease: "easeOut" as const, staggerChildren: 0.1}}
  };

  // Animation variants pour les éléments enfants
  const childVariants = {
    hidden: {opacity: 0, y: 20},
    visible: {opacity: 1, y: 0, transition: {duration: 0.4, ease: "easeOut" as const}}
  };

  // Animation variants pour le titre principal
  const titleVariants = {
    hidden: {opacity: 0, x: -30, scale: 0.9},
    visible: {opacity: 1, x: 0, scale: 1, transition: {duration: 0.7, ease: "easeOut" as const, delay: 0.2}}
  };

  // Animation variants pour les sous-titres
  const subtitleVariants = {
    hidden: {opacity: 0, y: 15},
    visible: {opacity: 1, y: 0, transition: {duration: 0.5, ease: "easeOut" as const}}
  };
  //#endregion

  return (
    <motion.div className="presta-card-wrapper" ref={cardRef} variants={cardVariants} initial="hidden" animate={isInView ? "visible" : "hidden"} 
      whileHover={{ 
        scale: 1.015,
        transition: { duration: 0.3 }
      }}
    >
      <NavLink to="/contact" className="presta-card" id={`presta-card-${id}`}>
        
        <motion.div className="presta-card-content" variants={childVariants}>
          <motion.h2 ref={titleRef} className="presta-card-title" variants={titleVariants} initial="hidden"
            animate={isTitleInView ? "visible" : "hidden"}
          >
            {title}
          </motion.h2>
          <motion.p className="presta-card-duration" variants={childVariants}>{duration}</motion.p>
          <motion.p className="presta-card-description" variants={childVariants}>{description}</motion.p>


          <motion.h3 variants={subtitleVariants}>Inclus</motion.h3>
          <motion.ul className="presta-card-included-services" variants={childVariants}>
            {includedServices.map((service, index) => (
              <motion.li key={index} variants={{hidden: { opacity: 0, x: -20 }, visible: {opacity: 1, x: 0, transition: {delay: index * 0.1, duration: 0.3}}}}>
                {service}
              </motion.li>
            ))}
          </motion.ul>


          <motion.h3 variants={subtitleVariants}>Caractéristiques</motion.h3>
          <motion.ul className="presta-card-characteristics" variants={childVariants}>
            {characteristics.map((char, index) => (
              <motion.li key={index} variants={{hidden: { opacity: 0, x: -20 }, visible: {opacity: 1, x: 0, transition: {delay: index * 0.1, duration: 0.3}}}}>
                {char}
              </motion.li>
            ))}
          </motion.ul>

          <motion.p className="presta-card-price" variants={{hidden: { opacity: 0, scale: 0.8 }, visible: {opacity: 1, scale: 1, transition: {duration: 0.5, ease: "easeOut" as const, delay: 0.3}}}}>
            {price}
          </motion.p>

        </motion.div>

        <motion.div 
          className="presta-card-image"
          style={{ backgroundImage: `url(${images[bgImage]})` }}
          variants={{
            hidden: {opacity: 0, scale: 1.1, x: 30},
            visible: {opacity: 1, scale: 1, x: 0, transition: {duration: 0.8, ease: "easeOut" as const, delay: 0.2}}
          }}
          whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
        />
      </NavLink>
    </motion.div>
  );
}
 
export default PrestaCard;